import { Component, Input } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateFormatPipe } from 'ngx-moment';
import { DuplicateItemActions } from 'src/app/enums/duplicate-item-actions.enum';
import { QuestionnaireMasterAvailability } from 'src/app/enums/questionnaire-master-availability.enum';
import { QuestionnaireMasterStatus } from 'src/app/enums/questionnaire-master-status.enum';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { DuplicateItemQfmSections } from './models/duplicate-item-qfm-sections.model';
import { DuplicateItemForm } from './models/duplicate-item-form.model';
import { MAX_QFM_TITLE_LENGTH } from 'src/app/constants/questionnaire.constants';
import { PagedResponse, SessionService } from '@edl-development/edl-common';

@Component({
    selector: 'edl-modal-duplicate-item',
    templateUrl: './edl-modal-duplicate-item.component.html',
    styleUrls:   ['./edl-modal-duplicate-item.component.scss'],
})
export class EdlModalDuplicateItem {
    @Input() modalTitle: string;
    @Input() itemTitle: string;
    @Input() maxTitleLength: number;
    @Input() questionnaireSections: DuplicateItemQfmSections[];
    @Input() status: QuestionnaireMasterStatus;
    @Input() availability: QuestionnaireMasterAvailability;
    @Input() isTemplate: boolean;
    @Input() toTemplate: boolean;

    form: UntypedFormGroup;
    questionnaires: any[]

    DuplicateItemActions = DuplicateItemActions;
    QuestionnaireMasterAvailability = QuestionnaireMasterAvailability;

    constructor(
        public activeModal: NgbActiveModal,
        private questionnaireService: QuestionnaireService,
        private sessionService: SessionService,
        private formBuilder: UntypedFormBuilder) {}

    ngOnInit() {
        if (this.questionnaireSections.length > 0) {
            this.questionnaireService
                .getQuestionnairesForPortal({
                    portalId: this.sessionService.user.UserPortal.PortalID,
                    availability: this.availability,
                    offset: 0,
                    limit: 10000,
                    status: this.status,
                    orderBy: 'Title',
                    ascending: true
                }).subscribe((results: PagedResponse) => {
                    this.questionnaires = results.Results;
                });
        }

        this.generateForm(this.questionnaireSections, this.itemTitle);
    }

    private generateForm(questionnaireSections: DuplicateItemQfmSections[], itemTitle: string) {
        const today = new Date();

        const sections = new UntypedFormArray([]);
        questionnaireSections?.forEach(section => {
            const questionnaires = new UntypedFormArray([]);

            section.questionnaires?.forEach(questionnaire => {
                const questionnaireForm = this.formBuilder.group({
                    action: [this.toTemplate !== questionnaire.IsTemplate ? DuplicateItemActions.DUPLICATE : DuplicateItemActions.KEEP, [Validators.required]],
                    oldQfmId: [questionnaire.QfmId, []],
                    newQfmId: [null, []],
                    duplicateQfmTitle: [this.generateTitle(questionnaire.QfmTitle, today, !questionnaire.IsTemplate, MAX_QFM_TITLE_LENGTH), [Validators.maxLength(MAX_QFM_TITLE_LENGTH)]],
                });
                questionnaireForm.setValidators(this.isQuestionnaireValid());
                questionnaires.push(questionnaireForm);
            });

            const sectionGroup = this.formBuilder.group({
                title: [section.title, []],
                questionnaires
            });

            sections.push(sectionGroup);
        });


        this.form = this.formBuilder.group({
            title: [this.generateTitle(itemTitle, today, this.toTemplate, this.maxTitleLength), [Validators.required, Validators.maxLength(this.maxTitleLength)]],
            shouldDuplicateRoster: [false, []],
            sections
        });
    }

    private generateTitle(baseTitle: string, today: Date, isTemplate: boolean, maxTotalTitleLength: number) {
        const dateFormatPipe = new DateFormatPipe();
        const postfix = ` ${ isTemplate ? 'Template' : 'Copy' } (${ dateFormatPipe.transform(today, 'MM/DD/YYYY hh:mm a') })`;
        const maxBaseTitleLength = maxTotalTitleLength - postfix.length;
        const adjustedBaseTitle = baseTitle.substring(0, maxBaseTitleLength);

        return adjustedBaseTitle + postfix;
    }

    public getQuestionnaireErrors(sectionIndex: number, quesitonnaireIndex: number, errorName: string) {
        const errors = this.getQuestionnaireGroup(sectionIndex, quesitonnaireIndex).errors;
        return errors ? errors[errorName] : null;
    } 

    public getQuestionnaireField(sectionIndex: number, quesitonnaireIndex: number, quesitonnaireGroupKey: string) {
        return this.getQuestionnaireGroup(sectionIndex, quesitonnaireIndex).get(quesitonnaireGroupKey);
    } 

    private getQuestionnaireGroup(sectionIndex: number, quesitonnaireIndex: number) {
        return this.form.get(['sections', sectionIndex, 'questionnaires', quesitonnaireIndex]);
    } 

    public submit({ value, valid }: { value: DuplicateItemForm, valid: boolean}) {
        if (valid) {
            this.activeModal.close(value);
        }
    }

    private isQuestionnaireValid(): ValidatorFn {
        return (c: AbstractControl): {[key: string]: any} => {
            const action = c.get('action').value;
            switch (action.toString()) {
                case DuplicateItemActions.REPLACE.toString():
                    return !c.get('newQfmId').value ? { newQfmIdRequired: true} : null;
                case DuplicateItemActions.DUPLICATE.toString():
                    return !c.get('duplicateQfmTitle').value ? { duplicateQfmTitleRequired: true} : null;
                case DuplicateItemActions.KEEP.toString():
                default:
                    return null;
            }
        };
    }
}